import Parse from 'parse';
import { multiEntityLoading } from '@/store/mixins/loading-states';

const CACHE_KEY = 'constructions_cache';
const CACHE_TIMESTAMP_KEY = 'constructions_cache_timestamp';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

const ConstructionModule = {
  namespaced: true,
  state: {
    ...multiEntityLoading.state,
    constructions: [],
    cacheTimestamp: null,
  },
  mutations: {
    ...multiEntityLoading.mutations,
    setConstructions(state, constructions) {
      state.constructions = constructions;
    },
    setCacheTimestamp(state, timestamp) {
      state.cacheTimestamp = timestamp;
    },
  },
  actions: {
    ...multiEntityLoading.actions,
    /**
     * Check if cache is still valid (less than 1 hour old)
     * @param {Object} context - Vuex context
     * @returns {boolean} - True if cache is valid, false otherwise
     */
    isCacheValid({ state }) {
      if (!state.cacheTimestamp) {
        // Try to get timestamp from localStorage if not in state
        const storedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (storedTimestamp) {
          const timestamp = parseInt(storedTimestamp, 10);
          const now = Date.now();
          const cacheAge = now - timestamp;
          return cacheAge < CACHE_DURATION;
        }
        return false;
      }

      const now = Date.now();
      const cacheAge = now - state.cacheTimestamp;
      return cacheAge < CACHE_DURATION;
    },

    /**
     * Load constructions from cache
     * @param {Object} context - Vuex context
     * @returns {Array|null} - Cached constructions or null if not available
     */
    loadConstructionsFromCache({ commit }) {
      try {
        const cachedData = localStorage.getItem(CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

        if (cachedData && cachedTimestamp) {
          const timestamp = parseInt(cachedTimestamp, 10);
          const now = Date.now();
          const cacheAge = now - timestamp;

          // Check if cache is still valid
          if (cacheAge < CACHE_DURATION) {
            console.log('[Construction Cache] Loading from cache, age:', Math.round(cacheAge / 1000 / 60), 'minutes');

            // Parse cached data and restore Parse Objects
            const parsedData = JSON.parse(cachedData);
            const parseObjects = parsedData.map(obj => {
              const parseObj = new Parse.Object('Baustellen');
              parseObj.id = obj.objectId;
              // Set attributes
              Object.keys(obj.attributes).forEach(key => {
                parseObj.set(key, obj.attributes[key]);
              });
              return parseObj;
            });

            commit('setConstructions', parseObjects);
            commit('setCacheTimestamp', timestamp);
            return parseObjects;
          } else {
            console.log('[Construction Cache] Cache expired, age:', Math.round(cacheAge / 1000 / 60), 'minutes');
            // Clear expired cache
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_TIMESTAMP_KEY);
          }
        } else {
          console.log('[Construction Cache] No cache found');
        }
      } catch (e) {
        console.error('[Construction Cache] Failed to load from cache:', e);
        // Clear corrupted cache
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      }
      return null;
    },

    /**
     * Save constructions to cache
     * @param {Object} context - Vuex context
     * @param {Array} constructions - Constructions to cache
     */
    saveConstructionsToCache({ commit }, constructions) {
      try {
        const timestamp = Date.now();

        // Convert Parse Objects to plain objects for storage
        const dataToCache = constructions.map(construction => ({
          objectId: construction.id,
          className: construction.className,
          attributes: construction.attributes
        }));

        localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString());
        commit('setCacheTimestamp', timestamp);

        console.log('[Construction Cache] Saved', constructions.length, 'constructions to cache');
      } catch (e) {
        console.error('[Construction Cache] Failed to save to cache:', e);
      }
    },

    /**
     * Clear constructions cache
     * @param {Object} context - Vuex context
     */
    clearConstructionsCache({ commit }) {
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        commit('setCacheTimestamp', null);
        console.log('[Construction Cache] Cache cleared');
      } catch (e) {
        console.error('[Construction Cache] Failed to clear cache:', e);
      }
    },

    /**
     * Load constructions with caching support
     * First tries to load from cache, then fetches from server if needed
     * @param {Object} context - Vuex context
     * @param {Object} options - Load options
     * @param {boolean} options.forceRefresh - Force refresh from server, bypass cache
     * @returns {Promise<Array>} - Array of constructions
     */
    async loadConstructions({ commit, dispatch }, options = {}) {
      const { forceRefresh = false } = options;

      try {
        // Try to load from cache first (unless force refresh requested)
        if (!forceRefresh) {
          const cachedConstructions = await dispatch('loadConstructionsFromCache');
          if (cachedConstructions && cachedConstructions.length > 0) {
            console.log('[Construction] Using cached constructions:', cachedConstructions.length);
            return cachedConstructions;
          }
        }

        // Fetch from server if cache miss or force refresh
        console.log('[Construction] Fetching constructions from Parse Server...');
        const query = new Parse.Query('Baustellen');
        query.limit(8000);
        query.equalTo('type', 'Auftrag');
        query.notEqualTo('statusBOQ', 'Abgeschlossen');
        query.notEqualTo('statusBOQ', 'Schlussgerechnet');
        query.addAscending('no');

        const con = await dispatch('handleLoadingState', {
          entity: 'constructions',
          promise: query.find(null),
        });

        // Save to cache
        commit('setConstructions', con);
        await dispatch('saveConstructionsToCache', con);

        console.log('[Construction] Fetched', con.length, 'constructions from server and cached');
        return con;
      } catch (e) {
        console.error('[Construction] Failed to load constructions:', e);
        commit('setConstructions', []);
        return [];
      }
    },
  },

  getters: {
    ...multiEntityLoading.getters,
    constructions: (state) => state.constructions,
    cacheAge: (state) => {
      if (!state.cacheTimestamp) {
        const storedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (storedTimestamp) {
          return Date.now() - parseInt(storedTimestamp, 10);
        }
        return null;
      }
      return Date.now() - state.cacheTimestamp;
    },
    isCacheValid: (state, getters) => {
      const age = getters.cacheAge;
      return age !== null && age < CACHE_DURATION;
    },
  },
};
export default ConstructionModule;
